import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Wallet, AlertTriangle } from "lucide-react";

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}

export default function TeamWalletsPage({ embedded = false, variant = "team" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const endpoint = variant === "others" ? "/me/other-wallets" : "/me/team-wallets";
  const emptyTitle = variant === "others"
    ? "No other wallets granted yet"
    : "No team wallets available";
  const emptyHint = variant === "others"
    ? <>You haven&apos;t been granted access to any individual wallets outside your team. An admin can pick wallets from your user profile under <span className="text-zinc-300">Module Access → Wallets → Visible Wallets</span>.</>
    : <>You don&apos;t share a team with anyone yet, or your wallets <span className="text-zinc-300">View Teammate&apos;s Items</span> permission is off.</>;
  const headerTitle = variant === "others" ? "Other Wallets" : "Team Wallets";
  const headerHint = variant === "others"
    ? "Wallets an admin has individually granted you access to. Tap a row to open the full ledger."
    : "Wallets of users in your team(s). Tap a row to open the full ledger.";
  const testidRoot = variant === "others" ? "other-wallets" : "team-wallets";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(endpoint)
      .then(({ data }) => { if (!cancelled) setItems(data || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint]);

  return (
    <div className="space-y-6" data-testid={`${testidRoot}-page`}>
      {!embedded && (
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="label-overline mb-1 flex items-center gap-2">
              <Wallet className="w-3.5 h-3.5" /> Wallets
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }}>
              {headerTitle}
            </h1>
            <p className="text-xs text-zinc-500 mt-1">{headerHint}</p>
          </div>
        </header>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading wallets…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-zinc-900 border border-white/10 rounded-lg p-6 flex items-start gap-3 text-zinc-400 text-sm" data-testid={`${testidRoot}-empty`}>
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-zinc-200 font-medium mb-1">{emptyTitle}</div>
            <div className="text-xs text-zinc-500 leading-relaxed">{emptyHint}</div>
          </div>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/50 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Wallet Number</th>
                <th className="text-left px-4 py-3 font-medium">Holder</th>
                <th className="text-right px-4 py-3 font-medium">Closing Balance</th>
                <th className="text-right px-4 py-3 font-medium">Unsettled</th>
                <th className="text-right px-4 py-3 font-medium">Entries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((w) => (
                <tr
                  key={w.id}
                  className="hover:bg-white/5 transition-colors"
                  data-testid={`${testidRoot}-row-${w.id}`}
                >
                  <td className="px-4 py-3 font-mono text-emerald-400">
                    <Link to={`/wallets/${w.id}`} className="hover:underline" data-testid={`${testidRoot}-link-${w.id}`}>
                      {w.wallet_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-zinc-200">{w.holder_name}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtINR(w.closing_balance)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${w.unsettled_total > 0 ? "text-amber-400" : "text-zinc-500"}`}>
                    {fmtINR(w.unsettled_total)}
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-400">{w.transaction_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
